import { Component, Input } from '@angular/core';
import { EventDoc } from '../../models/event.model';
import { locationLabel, primaryLocation } from '../event-utils';

/**
 * Venue logo + name + place, overlaid on a card image — iOS `BusinessInfoView`.
 *
 * Two styles, as on iOS: `clear` sits directly on the image (used on the featured card, which has a
 * dark scrim behind it), `filled` gets an opaque chip (used where there is no scrim).
 *
 * The second line is `locationLabel`, not the raw `city` iOS reads — `city` is empty on every
 * location in both projects, so iOS renders a blank line here today.
 */
@Component({
    selector: 'app-event-business-info',
    template: `
    <div class="ev-biz" [class.ev-biz-filled]="variant === 'filled'">
      <app-event-image class="ev-biz-logo" [url]="event.businessLogoUrl || null"
                       [alt]="event.businessName || ''"></app-event-image>
      <div class="ev-biz-text">
        <div class="ev-biz-name">{{ event.businessName }}</div>
        <div class="ev-biz-place" *ngIf="place">{{ place }}</div>
      </div>
    </div>
  `,
    styles: [`
    :host { display: block; }
    .ev-biz {
      display: flex;
      align-items: center;
      gap: 5px;
      padding: 4px 16px 4px 4px;
      min-width: 0;
    }
    .ev-biz-filled {
      background: #FFFFFF;
      border-radius: 20px 0 20px 0;
      box-shadow: 0 6px 10px rgba(19, 19, 19, 0.26);
    }
    .ev-biz-logo {
      width: 36px;
      height: 36px;
      flex: 0 0 auto;
      border-radius: 50%;
      border: 2px solid #FFFFFF;
      background: var(--ev-bg, #191919);
    }
    .ev-biz-text {
      min-width: 0;
    }
    .ev-biz-name,
    .ev-biz-place {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .ev-biz-name {
      font-size: 14px;
      font-weight: 700;
      color: var(--ev-on-surface, #FFFFFF);
    }
    .ev-biz-place {
      font-size: 12px;
      color: var(--ev-on-surface-variant, #CFCFCF);
    }
    /* On the white chip the text has to flip to dark, or it disappears. */
    .ev-biz-filled .ev-biz-name { color: #0C0C0C; }
    .ev-biz-filled .ev-biz-place { color: #5A5A5A; }
  `],
    standalone: false
})
export class EventBusinessInfoComponent {
  @Input() event!: EventDoc;

  /**
   * Named `variant`, NOT `style`: `style` is a native HTML attribute that Angular's compiler routes
   * to its styling instructions, so a static `style="filled"` on this element would be parsed as
   * CSS and the input would never receive it — silently, with no template error.
   */
  @Input() variant: 'clear' | 'filled' = 'clear';

  get place(): string {
    return locationLabel(primaryLocation(this.event));
  }
}
